import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { InvoiceStatus, InvoiceStatusMap } from "features/Invoices/constants/invoice.constants";
import { useInvoiceOverview } from "./InvoiceOverviewContext";
import OverviewTab from "./OverviewTab";
import {
  InvoiceOverviewTabParam,
  invoiceOverviewTabIdFromSearchParam,
  isValidInvoiceOverviewTabParam,
  searchParamForInvoiceOverviewTabId,
} from "./overviewTabSearchParams";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useViewInvoicePdf } from "features/Invoices/hooks/useViewInvoicePdf";
import { useInvoiceHasDirectPayment } from "features/Invoices/hooks/useInvoiceHasDirectPayment";
import { cancelInvoice } from "features/Invoices/services/InvoiceService";
import CancelInvoiceModal from "components/common/CancelInvoiceModal";

const Section = () => {
  const { can } = useAccess();
  const canReadInvoice = can(Permissions.INVOICE.READ);
  const canUpdateInvoice = can(Permissions.INVOICE.UPDATE);
  const canCancelInvoice = can(Permissions.INVOICE.CANCEL);
  const { viewPdf, loadingId: pdfLoadingId } = useViewInvoicePdf();
  const [searchParams, setSearchParams] = useSearchParams();
  const { invoice, refetch } = useInvoiceOverview();
  const [cancelOpen, setCancelOpen] = useState(false);

  const showCancelInvoice =
    canCancelInvoice &&
    invoice != null &&
    invoice.status !== InvoiceStatus.CANCELLED;

  const { hasDirectPayment } = useInvoiceHasDirectPayment(
    invoice?.id,
    showCancelInvoice,
  );

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidInvoiceOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  if (!invoice) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">This invoice could not be loaded.</p>
        </Col>
      </Row>
    );
  }

  const displayTitle = invoice.code?.trim() || `Invoice #${invoice.id}`;
  const activeTab = invoiceOverviewTabIdFromSearchParam(tabQuery);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForInvoiceOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === InvoiceOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3 align-items-start">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{displayTitle}</h4>
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={invoice.status}
                            map={InvoiceStatusMap}
                          />
                        </div>
                        <div className="vr d-none d-sm-block" />
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Due:</span>
                          <span className="fw-medium">
                            {invoice.dueDate ? (
                              <DateTime date={invoice.dueDate} />
                            ) : (
                              "—"
                            )}
                          </span>
                        </div>
                        <div className="vr d-none d-sm-block" />

                        {invoice.created_at ? (
                          <>
                            <div className="vr d-none d-sm-block" />
                            <div className="text-muted">
                              Published:{" "}
                              <span className="text-body fw-medium">
                                <DateTime date={invoice.created_at} />
                              </span>
                            </div>
                          </>
                        ) : null}
                        {invoice.deleted_at ? (
                          <>
                            <div className="vr d-none d-sm-block" />
                            <span
                              className="badge bg-danger-subtle text-danger"
                              title={invoice.deleted_at}
                            >
                              Deleted
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto mt-3 mt-md-0">
                    <div className="hstack gap-2 flex-nowrap">
                      {canUpdateInvoice &&
                      invoice.status !== InvoiceStatus.CANCELLED ? (
                        <Link
                          to={`/finance/invoices/edit/${invoice.id}`}
                          className="btn btn-primary btn-sm"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      ) : null}
                      {canReadInvoice ? (
                        <button
                          type="button"
                          className="btn btn-soft-secondary btn-sm"
                          onClick={() => viewPdf(invoice.id)}
                          disabled={pdfLoadingId != null}
                        >
                          {pdfLoadingId != null ? (
                            <Spinner size="sm" className="me-1" />
                          ) : (
                            <i className="ri-file-pdf-2-line align-bottom me-1" />
                          )}
                          Export PDF
                        </button>
                      ) : null}
                      {showCancelInvoice ? (
                        <button
                          type="button"
                          className="btn btn-soft-danger btn-sm"
                          onClick={() => setCancelOpen(true)}
                          disabled={hasDirectPayment}
                          title={
                            hasDirectPayment
                              ? "This invoice has a direct payment applied, so it can't be cancelled. Cancel that payment first."
                              : undefined
                          }
                        >
                          <i className="ri-close-circle-line align-bottom me-1" />
                          Cancel invoice
                        </button>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
      <CancelInvoiceModal
        show={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onSuccess={refetch}
        submit={(payload) => {
          if (!invoice) throw new Error("No invoice loaded.");
          return cancelInvoice(invoice.id, payload);
        }}
      />
    </React.Fragment>
  );
};

export default Section;
